import { useState, useMemo, useRef, useEffect } from "react";
import type { ComponentType, ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { Moon, LogOut, Menu, Search, X } from "./icons.js";
import { FeedbackButton } from "./FeedbackButton.js";
import { useTheme } from "../lib/theme.js";

/** Sidebar pin preference (QA #80) — remembered across sessions, shared by both personas. */
const NAV_PIN_KEY = "th:nav-pinned";
const loadPinned = (): boolean => {
  try {
    return localStorage.getItem(NAV_PIN_KEY) !== "0";
  } catch {
    return true;
  }
};

export interface NavItem {
  to: string;
  label: string;
  Icon: ComponentType<{ className?: string }>;
  /** exact match for index routes */
  end?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

export interface AppShellProps {
  /** persona brand name shown in the sidebar, e.g. "TalentHop" */
  brand: string;
  brandTag?: string;
  /** accent color CSS value driving --accent (per-persona) */
  accent?: string;
  groups: NavGroup[];
  /** whether to show sidebar search bar (default: true) */
  showSearch?: boolean;
  /** org / context label in the topbar */
  orgLabel?: string;
  orgInitials?: string;
  roleBadge?: string;
  userInitials?: string;
  /** optional footer action in the sidebar */
  footer?: ReactNode;
  /** when provided, renders a sign-out control in the topbar */
  onLogout?: () => void;
  children: ReactNode;
}

export function AppShell({
  brand,
  brandTag,
  accent,
  groups,
  showSearch = true,
  orgLabel,
  orgInitials,
  roleBadge,
  // "–", never a real-looking default: a hardcoded "HA" showed for every user (QA #79).
  userInitials = "–",
  footer,
  onLogout,
  children,
}: AppShellProps): ReactNode {
  const { cycle } = useTheme();
  const [pinned, setPinned] = useState(loadPinned);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  // Hover labels for sidebar controls. The sidebar scrolls (overflow-y), which would clip a CSS
  // data-tip overlay, so the rail renders one fixed-position label beside the hovered control.
  const [railTip, setRailTip] = useState<{ label: string; top: number; left: number } | null>(null);
  const railTipProps = (label: string | undefined) =>
    label
      ? {
          onMouseEnter: (e: React.MouseEvent<HTMLElement>) => showRailTip(label, e.currentTarget),
          onFocus: (e: React.FocusEvent<HTMLElement>) => showRailTip(label, e.currentTarget),
          onMouseLeave: () => setRailTip(null),
          onBlur: () => setRailTip(null),
        }
      : {};
  const showRailTip = (label: string, el: HTMLElement): void => {
    const r = el.getBoundingClientRect();
    setRailTip({ label, top: r.top + r.height / 2, left: r.right + 10 });
  };

  const togglePinned = (): void =>
    setPinned((v) => {
      try {
        localStorage.setItem(NAV_PIN_KEY, v ? "0" : "1");
      } catch {
        /* preference is best-effort */
      }
      return !v;
    });

  // Shortcut keys '/' or 'Cmd/Ctrl + K' to quickly focus sidebar search; 'Escape' to clear
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const isInputFocused =
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        (e.target as HTMLElement)?.isContentEditable;

      if ((e.key === "/" && !isInputFocused) || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k")) {
        e.preventDefault();
        if (!pinned) {
          setPinned(true);
        }
        setTimeout(() => searchInputRef.current?.focus(), 50);
      } else if (e.key === "Escape" && document.activeElement === searchInputRef.current) {
        setSearchQuery("");
        searchInputRef.current?.blur();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [pinned]);

  // Filter groups and items based on search query
  const filteredGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return groups;

    return groups
      .map((g) => ({
        ...g,
        items: g.items.filter((item) => {
          const matchLabel = item.label.toLowerCase().includes(q);
          const matchTo = item.to.toLowerCase().includes(q);
          const matchGroup = g.label.toLowerCase().includes(q);
          return matchLabel || matchTo || matchGroup;
        }),
      }))
      .filter((g) => g.items.length > 0);
  }, [groups, searchQuery]);

  const style = accent ? ({ ["--accent" as string]: accent } as React.CSSProperties) : undefined;

  return (
    <div
      className={`app t1-admin-shell${pinned ? "" : " nav-collapsed"}${mobileOpen ? " mobile-nav-open" : ""}`}
      style={style}
    >
      {/* Below 860px the sidebar becomes an off-canvas drawer (see app.css); this dims and
          closes it on outside-tap, same as any standard mobile nav. */}
      {mobileOpen && (
        <div className="mobile-nav-backdrop" onClick={() => setMobileOpen(false)} aria-hidden="true" />
      )}
      <aside className="sidebar">
        <div className="brand">
          {/* Theme 1: the same lime "TH" mark as the customer rail. */}
          <span className="mark" aria-hidden="true">
            TH
          </span>
          <span className="name">
            {brand} {brandTag && <span className="v">{brandTag}</span>}
          </span>
          {/* QA #80 — collapse to an icon rail; the preference sticks across sessions. */}
          <button
            type="button"
            className="nav-pin"
            {...railTipProps(pinned ? "Collapse the navigation" : "Expand the navigation")}
            aria-label={pinned ? "Collapse the navigation" : "Expand the navigation"}
            aria-pressed={pinned}
            onClick={togglePinned}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 15, height: 15, transform: pinned ? "none" : "rotate(180deg)", transition: "transform .15s" }}>
              <path d="M15 6l-6 6 6 6" />
            </svg>
          </button>
        </div>

        {/* Sidebar Search Input */}
        {showSearch && (
          <div className="sidebar-search-container">
            {pinned ? (
              <div className="sidebar-search-wrap">
                <Search className="sidebar-search-icon" />
                <input
                  ref={searchInputRef}
                  type="text"
                  className="sidebar-search-input"
                  placeholder="Search pages... (/)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search navigation pages"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="sidebar-search-clear"
                    onClick={() => setSearchQuery("")}
                    {...railTipProps("Clear search")}
                    aria-label="Clear search"
                  >
                    <X style={{ width: 12, height: 12 }} />
                  </button>
                )}
              </div>
            ) : (
              <button
                type="button"
                className="sidebar-search-collapsed-btn"
                onClick={() => {
                  togglePinned();
                  setTimeout(() => searchInputRef.current?.focus(), 50);
                }}
                {...railTipProps("Search navigation (/)")}
                aria-label="Expand and search navigation"
              >
                <Search style={{ width: 16, height: 16 }} />
              </button>
            )}
          </div>
        )}

        {filteredGroups.map((group) => (
          <div key={group.label}>
            <div className="navlbl">{group.label}</div>
            <nav className="nav">
              {group.items.map(({ to, label, Icon, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  aria-label={pinned ? undefined : label}
                  {...railTipProps(pinned ? undefined : label)}
                  className={({ isActive }) => (isActive ? "on" : "")}
                  onClick={() => {
                    setMobileOpen(false);
                    setRailTip(null);
                  }}
                >
                  <Icon />
                  <span className="nav-label">{label}</span>
                </NavLink>
              ))}
            </nav>
          </div>
        ))}

        {searchQuery.trim() && filteredGroups.length === 0 && (
          <div className="sidebar-search-empty">
            <span>No pages matching "{searchQuery}"</span>
            <button
              type="button"
              className="sidebar-search-reset-link"
              onClick={() => setSearchQuery("")}
            >
              Clear filter
            </button>
          </div>
        )}

        {footer && <div className="side-foot">{footer}</div>}
      </aside>
      {railTip && (
        <div className="t1-rail-tip" role="presentation" style={{ top: railTip.top, left: railTip.left }}>
          {railTip.label}
        </div>
      )}

      <div className="main">
        <div className="topbar">
          {/* Only visible below 860px (app.css) — the sidebar is off-canvas there. */}
          <button
            type="button"
            className="mobile-menu-btn"
            data-tip="Open navigation"
            data-tip-pos="right"
            aria-label="Open navigation"
            onClick={() => setMobileOpen(true)}
          >
            <Menu />
          </button>
          {orgLabel && (
            <div className="org">
              {orgInitials && <span className="logo">{orgInitials}</span>}
              {orgLabel}
            </div>
          )}
          <div className="spacer" />
          {roleBadge && <span className="rolebadge">{roleBadge}</span>}
          {/* 0081: one note from any page, read in the platform console under Product feedback. */}
          <FeedbackButton />
          <button className="theme-toggle" data-tip="Toggle theme" onClick={cycle} aria-label="Toggle theme">
            <Moon />
          </button>
          {onLogout && (
            <button
              className="theme-toggle"
              data-tip="Sign out"
              data-tip-pos="left"
              onClick={onLogout}
              aria-label="Sign out"
            >
              <LogOut />
            </button>
          )}
          <div className="avatar">{userInitials}</div>
        </div>
        <div className="content">{children}</div>
      </div>
    </div>
  );
}
